// Repertoire: the pieces a student is working on.
//
// The rules a phone shares with `src/data/useRepertoire.jsx` and the Add
// Repertoire drawer: adding a piece, moving its status, listing a student's
// pieces. The provider keeps the batch.
//
// Status is the useful field: a teacher wants to know at a glance what is being
// worked on versus what is finished, and the report is mostly a roll-up of it.
//
// **Adapters**: the rules are `core/src/practice.rs`, and
// `REPERTOIRE_STATUSES` stays for the selects that read it at module scope,
// pinned to the core's (`test/domain/lessonCore.test.js`).
import { coreSync } from './core.js';
import { documentFromRows } from './documents.js';

const str = (v) => (v == null ? null : String(v));

export const REPERTOIRE_STATUSES = Object.freeze([
  Object.freeze({ id: 'assigned', label: 'Assigned' }),
  Object.freeze({ id: 'in_progress', label: 'In Progress' }),
  Object.freeze({ id: 'polishing', label: 'Polishing' }),
  Object.freeze({ id: 'completed', label: 'Completed' }),
]);

// The longest title firestore.rules stores (`size() < 300`), for the Title
// box's `maxLength`; pinned to the core's (`test/domain/lessonCore.test.js`).
export const REPERTOIRE_TITLE_MAX = 299;

// Save is offered once the title holds something other than spaces, and no
// more than the rules store (RepertoirePieceDrawer.jsx).
export function repertoireCanSave(title) {
  return coreSync().repertoireCanSave(String(title ?? ''));
}

// The Title box's refusal, or null: a title past the bound, which the rules
// would refuse with nothing on screen. The box's `maxLength` stops typing at
// the bound; this is for what gets past it.
export function repertoireTitleError(title) {
  return coreSync().repertoireTitleError(String(title ?? ''))?.message ?? null;
}

// The piece a Save appends. The title as typed, `Untitled` when empty; the
// status the drawer chose, `assigned` when none; assigned on `today`.
export function repertoirePieceDocument({ studentId, title, composer, status, note }, today, serverTimestamp) {
  return documentFromRows(coreSync().repertoirePieceDocument(
    String(studentId), str(title), str(composer), str(status), str(note), String(today),
  ), serverTimestamp);
}

// A status change: the merging set the table's select writes.
export function repertoireStatusDocument(status, serverTimestamp) {
  return documentFromRows(coreSync().repertoireStatusDocument(String(status)), serverTimestamp);
}

// A student's pieces as the tab lists them: by title, in `localeCompare` order.
export function sortRepertoire(pieces = []) {
  return coreSync().sortRepertoire(pieces.map((p) => String(p.title || ''))).map((i) => pieces[i]);
}
